/**
 * A comma-separated To field, as a list of addresses (#602).
 *
 * Split on commas and semicolons — what people paste out of Outlook — and de-duplicated
 * case-insensitively, keeping the first spelling typed, because `Lisa@x.com` and `lisa@x.com`
 * are one person and the server would otherwise mail them twice.
 */
export const parseRecipientList = (value: string): string[] => {
	const seen = new Set<string>();
	const out: string[] = [];
	for (const raw of value.split(/[,;]/)) {
		const email = raw.trim();
		if (!email || seen.has(email.toLowerCase())) continue;
		seen.add(email.toLowerCase());
		out.push(email);
	}
	return out;
};

export const formatRecipientList = (emails: string[]): string => parseRecipientList(emails.join(',')).join(', ');

/** Whether `email` is already in the field, ignoring case. */
export const hasRecipient = (value: string, email: string): boolean =>
	parseRecipientList(value).some((existing) => existing.toLowerCase() === email.toLowerCase());

/**
 * Add `email` if absent, remove it if present — leaving every other address, including ones
 * typed by hand, exactly where it was.
 */
export const toggleRecipient = (value: string, email: string): string => {
	const list = parseRecipientList(value);
	return hasRecipient(value, email)
		? formatRecipientList(list.filter((existing) => existing.toLowerCase() !== email.toLowerCase()))
		: formatRecipientList([...list, email]);
};
